/**
 * ConfigCostosForm — edita `costos_config/proveedores` (solo admin).
 *
 * Plan de LiveKit, tabla de BigQuery con la factura de Google, costos fijos
 * mensuales y precios de lista. Si el documento no existe, el formulario
 * muestra los valores por defecto que usa el backend (PRECIOS_DEFECTO y
 * PLAN_LIVEKIT_DEFECTO de functions/src/costos/tipos.ts, copiados en costosUtil).
 * Guardar solo afecta cálculos nuevos: el costo ya registrado no se recalcula.
 */
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Loader2, Plus, Save, Settings, Trash2, RotateCcw } from 'lucide-react';
import dashStyles from '../../pages/admin/AdminDashboardPage.module.css';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { categoriaLabel } from '../../utils/usoIaFormato';
import {
  CATEGORIAS, PLAN_LIVEKIT_DEFECTO, PRECIOS_DEFECTO, PRECIOS_ETIQUETAS,
  type CategoriaServicio, type ClavePrecio, type CostoFijoForm,
} from './costosUtil';
import styles from './ServiciosUsoPanel.module.css';

type CampoPlan = 'cuotaMensualUsd' | 'minutosIncluidos' | 'gbIncluidos' | 'egressPistaMinIncluidos' | 'sesionLargaMin';

interface FormState {
  plan: 'ship' | 'scale' | 'build';
  numeros: Record<CampoPlan, string>;
  projectId: string;
  bq: { proyecto: string; dataset: string; tabla: string };
  fijos: CostoFijoForm[];
  precios: Record<ClavePrecio, string>;
}

const CAMPOS_PLAN: Array<{ clave: CampoPlan; etiqueta: string; ayuda: string; min?: number }> = [
  { clave: 'cuotaMensualUsd', etiqueta: 'Cuota mensual del plan (USD)', ayuda: 'Lo que cobra LiveKit al mes por el plan.' },
  { clave: 'minutosIncluidos', etiqueta: 'Minutos de participante incluidos', ayuda: 'Por mes.' },
  { clave: 'gbIncluidos', etiqueta: 'Transferencia incluida (GB)', ayuda: 'Por mes.' },
  { clave: 'egressPistaMinIncluidos', etiqueta: 'Minutos de grabación por pista incluidos', ayuda: 'Por mes.' },
  { clave: 'sesionLargaMin', etiqueta: 'Minutos para considerar una sesión larga', ayuda: 'Una sala abierta más de este tiempo genera alerta. Mínimo 1.', min: 1 },
];

const PLANES: Array<{ valor: FormState['plan']; texto: string }> = [
  { valor: 'build', texto: 'Build' },
  { valor: 'ship', texto: 'Ship' },
  { valor: 'scale', texto: 'Scale' },
];

const REGEX_BQ = /^[A-Za-z0-9_.:-]*$/;

function preciosPorDefecto(): Record<ClavePrecio, string> {
  return Object.fromEntries(
    (Object.keys(PRECIOS_DEFECTO) as ClavePrecio[]).map((k) => [k, String(PRECIOS_DEFECTO[k])]),
  ) as Record<ClavePrecio, string>;
}

function formPorDefecto(): FormState {
  return {
    plan: PLAN_LIVEKIT_DEFECTO.plan,
    numeros: {
      cuotaMensualUsd: String(PLAN_LIVEKIT_DEFECTO.cuotaMensualUsd),
      minutosIncluidos: String(PLAN_LIVEKIT_DEFECTO.minutosIncluidos),
      gbIncluidos: String(PLAN_LIVEKIT_DEFECTO.gbIncluidos),
      egressPistaMinIncluidos: String(PLAN_LIVEKIT_DEFECTO.egressPistaMinIncluidos),
      sesionLargaMin: String(PLAN_LIVEKIT_DEFECTO.sesionLargaMin),
    },
    projectId: '',
    bq: { proyecto: '', dataset: '', tabla: '' },
    fijos: [],
    precios: preciosPorDefecto(),
  };
}

const valido = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;

/** Mezcla lo guardado con los valores por defecto (mismo criterio que el backend). */
function formDesdeDoc(d: Record<string, unknown> | undefined): FormState {
  const base = formPorDefecto();
  if (!d) return base;
  const p = (d.precios ?? {}) as Record<string, unknown>;
  for (const k of Object.keys(base.precios) as ClavePrecio[]) {
    if (valido(p[k])) base.precios[k] = String(p[k]);
  }
  const l = (d.livekit ?? {}) as Record<string, unknown>;
  if (l.plan === 'ship' || l.plan === 'scale' || l.plan === 'build') base.plan = l.plan;
  for (const c of CAMPOS_PLAN) {
    if (valido(l[c.clave])) base.numeros[c.clave] = String(l[c.clave]);
  }
  if (typeof l.projectId === 'string') base.projectId = l.projectId;
  const b = (d.bigquery ?? {}) as Record<string, unknown>;
  base.bq = {
    proyecto: typeof b.proyecto === 'string' ? b.proyecto : '',
    dataset: typeof b.dataset === 'string' ? b.dataset : '',
    tabla: typeof b.tabla === 'string' ? b.tabla : '',
  };
  if (Array.isArray(d.fijos)) {
    base.fijos = (d.fijos as Array<Record<string, unknown>>)
      .filter((f) => typeof f?.id === 'string' && f.id)
      .map((f) => ({
        id: f.id as string,
        nombre: typeof f.nombre === 'string' ? f.nombre : (f.id as string),
        mensualUsd: valido(f.mensualUsd) ? String(f.mensualUsd) : '0',
        categoria: (CATEGORIAS as string[]).includes(f.categoria as string) ? (f.categoria as CategoriaServicio) : 'fijo',
      }));
  }
  return base;
}

function validarNumero(txt: string, min = 0): string | null {
  if (txt.trim() === '') return 'Obligatorio';
  const n = Number(txt);
  if (!Number.isFinite(n)) return 'Debe ser un número';
  if (n < min) return min > 0 ? `Debe ser ${min} o más` : 'Debe ser 0 o más';
  return null;
}

function validarForm(f: FormState): Record<string, string> {
  const e: Record<string, string> = {};
  for (const c of CAMPOS_PLAN) {
    const m = validarNumero(f.numeros[c.clave], c.min ?? 0);
    if (m) e[`plan.${c.clave}`] = m;
  }
  for (const k of Object.keys(f.precios) as ClavePrecio[]) {
    const m = validarNumero(f.precios[k]);
    if (m) e[`precio.${k}`] = m;
  }
  (['proyecto', 'dataset', 'tabla'] as const).forEach((k) => {
    if (!REGEX_BQ.test(f.bq[k])) e[`bq.${k}`] = 'Solo letras, números, punto, guion y guion bajo';
  });
  f.fijos.forEach((x, i) => {
    if (!x.nombre.trim()) e[`fijo.${i}.nombre`] = 'Obligatorio';
    const m = validarNumero(x.mensualUsd);
    if (m) e[`fijo.${i}.mensualUsd`] = m;
  });
  return e;
}

const slug = (t: string) =>
  t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'fijo';

function Campo({ id, etiqueta, ayuda, error, children }: {
  id: string; etiqueta: string; ayuda?: string; error?: string; children: ReactNode;
}) {
  return (
    <div className={styles.campo}>
      <label htmlFor={id}>{etiqueta}</label>
      {children}
      {ayuda && !error && <span id={`${id}-ayuda`} className={styles.muted}>{ayuda}</span>}
      {error && <span id={`${id}-err`} className={styles.campoErr} role="alert">{error}</span>}
    </div>
  );
}

export default function ConfigCostosForm() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [form, setForm] = useState<FormState>(formPorDefecto);
  const [existe, setExiste] = useState<boolean | null>(null);
  const [cargando, setCargando] = useState(false);
  const [cargado, setCargado] = useState(false);
  const [errorLectura, setErrorLectura] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState<Record<string, string>>({});

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorLectura(false);
    try {
      const snap = await getDoc(doc(db, 'costos_config', 'proveedores'));
      setExiste(snap.exists());
      setForm(formDesdeDoc(snap.exists() ? (snap.data() as Record<string, unknown>) : undefined));
      setCargado(true);
    } catch (err) {
      console.error('[ConfigCostosForm] no se pudo leer costos_config/proveedores', err);
      setErrorLectura(true);
    } finally {
      setCargando(false);
    }
  }, []);

  // Se carga al abrir la sección (evita lecturas si nadie la despliega).
  const [abierto, setAbierto] = useState(false);
  useEffect(() => { if (abierto && !cargado && !cargando) void cargar(); }, [abierto, cargado, cargando, cargar]);

  const setPlanNum = (k: CampoPlan, v: string) => setForm((f) => ({ ...f, numeros: { ...f.numeros, [k]: v } }));
  const setPrecio = (k: ClavePrecio, v: string) => setForm((f) => ({ ...f, precios: { ...f.precios, [k]: v } }));
  const setFijo = (i: number, parcial: Partial<CostoFijoForm>) =>
    setForm((f) => ({ ...f, fijos: f.fijos.map((x, j) => (j === i ? { ...x, ...parcial } : x)) }));

  async function guardar(ev: FormEvent) {
    ev.preventDefault();
    const e = validarForm(form);
    setErrores(e);
    if (Object.keys(e).length > 0) {
      addToast('error', 'Revise los campos marcados: hay valores que no son válidos.');
      return;
    }
    setGuardando(true);
    try {
      const usados = new Set(form.fijos.map((x) => x.id).filter(Boolean));
      const fijos = form.fijos.map((x) => {
        let id = x.id;
        if (!id) {
          const base = slug(x.nombre);
          id = base;
          for (let n = 2; usados.has(id); n++) id = `${base}_${n}`;
          usados.add(id);
        }
        return { id, nombre: x.nombre.trim(), mensualUsd: Number(x.mensualUsd), categoria: x.categoria };
      });
      await setDoc(doc(db, 'costos_config', 'proveedores'), {
        precios: Object.fromEntries((Object.keys(form.precios) as ClavePrecio[]).map((k) => [k, Number(form.precios[k])])),
        livekit: {
          plan: form.plan,
          cuotaMensualUsd: Number(form.numeros.cuotaMensualUsd),
          minutosIncluidos: Number(form.numeros.minutosIncluidos),
          gbIncluidos: Number(form.numeros.gbIncluidos),
          egressPistaMinIncluidos: Number(form.numeros.egressPistaMinIncluidos),
          sesionLargaMin: Number(form.numeros.sesionLargaMin),
          projectId: form.projectId.trim(),
        },
        bigquery: {
          proyecto: form.bq.proyecto.trim(),
          dataset: form.bq.dataset.trim(),
          tabla: form.bq.tabla.trim(),
        },
        fijos,
        actualizadoEn: serverTimestamp(),
        actualizadoPor: user?.uid ?? null,
      }, { merge: true });
      setExiste(true);
      setForm((f) => ({ ...f, fijos: f.fijos.map((x, i) => ({ ...x, id: fijos[i].id })) }));
      addToast('success', 'Configuración de costos guardada. Se aplica a los cálculos nuevos; no recalcula lo ya registrado.');
    } catch (err) {
      console.error('[ConfigCostosForm] error al guardar', err);
      addToast('error', 'No se pudo guardar la configuración de costos.');
    } finally {
      setGuardando(false);
    }
  }

  const cantErrores = Object.keys(errores).length;
  const inputNum = (id: string, valor: string, onChange: (v: string) => void, error?: string, step = 'any') => (
    <input
      id={id}
      type="number"
      inputMode="decimal"
      min="0"
      step={step}
      value={valor}
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${id}-err` : `${id}-ayuda`}
    />
  );

  return (
    <div className={dashStyles.section}>
      <div className={dashStyles.sectionHeader}>
        <h2 className={dashStyles.sectionTitle}><Settings size={16} aria-hidden="true" /> Configuración de costos</h2>
        <span className={dashStyles.sectionBadge}>solo administrador</span>
      </div>
      <div className={dashStyles.sectionBody}>
        <details className={styles.detallesCfg} onToggle={(e) => setAbierto((e.currentTarget as HTMLDetailsElement).open)}>
          <summary>Plan de LiveKit, factura de Google, costos fijos y precios de lista</summary>

          {cargando && !cargado ? (
            <p className={dashStyles.emptyState}>Cargando configuración…</p>
          ) : errorLectura ? (
            <p className={styles.errorBox} role="alert">
              No se pudo leer la configuración. <button type="button" className={`${styles.accionBtn} ${styles.accionSec}`} onClick={cargar}>Reintentar</button>
            </p>
          ) : (
            <form onSubmit={guardar} noValidate>
              {existe === false && (
                <p className={`${styles.nota} ${styles.notaSuave}`} style={{ marginTop: 'var(--space-3)' }}>
                  Todavía no hay configuración guardada: se muestran los valores por defecto que usa el sistema.
                  Al guardar se crea el documento.
                </p>
              )}

              <fieldset className={styles.cfgGrupo}>
                <legend>Plan de LiveKit</legend>
                <div className={styles.cfgCampos}>
                  <Campo id="cfg-plan" etiqueta="Plan contratado">
                    <select id="cfg-plan" value={form.plan} onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value as FormState['plan'] }))}>
                      {PLANES.map((p) => <option key={p.valor} value={p.valor}>{p.texto}</option>)}
                    </select>
                  </Campo>
                  {CAMPOS_PLAN.map((c) => (
                    <Campo key={c.clave} id={`cfg-${c.clave}`} etiqueta={c.etiqueta} ayuda={c.ayuda} error={errores[`plan.${c.clave}`]}>
                      {inputNum(`cfg-${c.clave}`, form.numeros[c.clave], (v) => setPlanNum(c.clave, v), errores[`plan.${c.clave}`])}
                    </Campo>
                  ))}
                  <Campo id="cfg-projectId" etiqueta="Identificador de proyecto de LiveKit" ayuda="Empieza con p_. Solo se necesita en el plan Scale.">
                    <input id="cfg-projectId" type="text" value={form.projectId} autoComplete="off"
                      onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))} aria-describedby="cfg-projectId-ayuda" />
                  </Campo>
                </div>
              </fieldset>

              <fieldset className={styles.cfgGrupo}>
                <legend>Factura real de Google (exportación a BigQuery)</legend>
                <div className={styles.cfgCampos}>
                  {(['proyecto', 'dataset', 'tabla'] as const).map((k) => (
                    <Campo key={k} id={`cfg-bq-${k}`} etiqueta={k === 'proyecto' ? 'Proyecto' : k === 'dataset' ? 'Conjunto de datos (dataset)' : 'Tabla'}
                      error={errores[`bq.${k}`]}>
                      <input id={`cfg-bq-${k}`} type="text" value={form.bq[k]} autoComplete="off"
                        onChange={(e) => setForm((f) => ({ ...f, bq: { ...f.bq, [k]: e.target.value } }))}
                        aria-invalid={errores[`bq.${k}`] ? true : undefined}
                        aria-describedby={errores[`bq.${k}`] ? `cfg-bq-${k}-err` : undefined} />
                    </Campo>
                  ))}
                </div>
              </fieldset>

              <fieldset className={styles.cfgGrupo}>
                <legend>Costos fijos mensuales</legend>
                {form.fijos.length === 0 && <p className={styles.muted}>No hay costos fijos. Agregue los que se pagan cada mes (dominios, licencias, servidores…).</p>}
                {form.fijos.map((x, i) => (
                  <div key={x.id || `nuevo-${i}`} className={styles.fijoFila}>
                    <Campo id={`cfg-fijo-${i}-nombre`} etiqueta="Nombre" error={errores[`fijo.${i}.nombre`]}>
                      <input id={`cfg-fijo-${i}-nombre`} type="text" value={x.nombre} onChange={(e) => setFijo(i, { nombre: e.target.value })}
                        aria-invalid={errores[`fijo.${i}.nombre`] ? true : undefined}
                        aria-describedby={errores[`fijo.${i}.nombre`] ? `cfg-fijo-${i}-nombre-err` : undefined} />
                    </Campo>
                    <Campo id={`cfg-fijo-${i}-monto`} etiqueta="USD por mes" error={errores[`fijo.${i}.mensualUsd`]}>
                      {inputNum(`cfg-fijo-${i}-monto`, x.mensualUsd, (v) => setFijo(i, { mensualUsd: v }), errores[`fijo.${i}.mensualUsd`])}
                    </Campo>
                    <Campo id={`cfg-fijo-${i}-cat`} etiqueta="Categoría">
                      <select id={`cfg-fijo-${i}-cat`} value={x.categoria} onChange={(e) => setFijo(i, { categoria: e.target.value as CategoriaServicio })}>
                        {CATEGORIAS.map((c) => <option key={c} value={c}>{categoriaLabel(c)}</option>)}
                      </select>
                    </Campo>
                    <button type="button" className={`${styles.accionBtn} ${styles.accionSec}`}
                      onClick={() => setForm((f) => ({ ...f, fijos: f.fijos.filter((_, j) => j !== i) }))}
                      aria-label={`Quitar el costo fijo ${x.nombre || i + 1}`}>
                      <Trash2 size={14} aria-hidden="true" /> Quitar
                    </button>
                  </div>
                ))}
                <button type="button" className={`${styles.accionBtn} ${styles.accionSec}`}
                  onClick={() => setForm((f) => ({ ...f, fijos: [...f.fijos, { id: '', nombre: '', mensualUsd: '0', categoria: 'fijo' }] }))}>
                  <Plus size={14} aria-hidden="true" /> Agregar costo fijo
                </button>
              </fieldset>

              <fieldset className={styles.cfgGrupo}>
                <legend>Precios de lista (USD)</legend>
                <div className={styles.cfgCampos}>
                  {PRECIOS_ETIQUETAS.map((p) => (
                    <Campo key={p.clave} id={`cfg-precio-${p.clave}`} etiqueta={p.etiqueta} ayuda={p.unidad} error={errores[`precio.${p.clave}`]}>
                      {inputNum(`cfg-precio-${p.clave}`, form.precios[p.clave], (v) => setPrecio(p.clave, v), errores[`precio.${p.clave}`])}
                    </Campo>
                  ))}
                </div>
                <div className={styles.cfgAcciones}>
                  <button type="button" className={`${styles.accionBtn} ${styles.accionSec}`}
                    onClick={() => setForm((f) => ({ ...f, precios: preciosPorDefecto() }))}>
                    <RotateCcw size={14} aria-hidden="true" /> Restaurar precios de lista por defecto
                  </button>
                </div>
              </fieldset>

              {cantErrores > 0 && (
                <p className={`${styles.errorBox} ${styles.resumenErrores}`} role="alert">
                  Hay {cantErrores} {cantErrores === 1 ? 'campo' : 'campos'} con valores no válidos. Corríjalos para guardar.
                </p>
              )}
              <div className={styles.cfgAcciones}>
                <button type="submit" className={styles.accionBtn} disabled={guardando}>
                  {guardando ? <Loader2 size={14} className={dashStyles.spinning} aria-hidden="true" /> : <Save size={14} aria-hidden="true" />}
                  Guardar configuración
                </button>
              </div>
              <p className={styles.muted} style={{ marginTop: 'var(--space-2)' }}>
                Los cambios se aplican a los cálculos nuevos (el sistema los lee cada pocos minutos). El costo ya registrado no se recalcula.
              </p>
            </form>
          )}
        </details>
      </div>
    </div>
  );
}
